import cloneDeep from 'lodash/cloneDeep';
import includes from 'lodash/includes';
import forOwn from 'lodash/forOwn';
import isEqual from 'lodash/isEqual';
import get from 'lodash/get';
import omit from 'lodash/omit';
import isArray from 'lodash/isArray';
import isString from 'lodash/isString';
import isNil from 'lodash/isNil';
import isBoolean from 'lodash/isBoolean';
import some from 'lodash/some';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { map, filter, distinctUntilChanged } from 'rxjs/operators';
import { IFilterModel } from '../models/i-filter.model';
import { IFilterApiModel } from '../models/i-filter-api.model';
import { TranslateService } from '@ngx-translate/core';
import { ALLERGEN_ID_NAME_MAP } from '../../constants';

@Injectable()
export class SearchCakesService {
  static FILTERS_API_KEYS_MAP: any = {
    // to api
    tags: 'tag',
    allergens: 'allergen',
    productGroups: 'group',

    // to local
    tag: 'tags',
    allergen: 'allergens',
    group: 'productGroups',

    hasMotive: 'hasMotive',
    postNumber: 'postNumber',
    categoryId: 'categoryId'
  };

  constructor(private _translate: TranslateService) {
  }

  public criteriaChanged$ = new BehaviorSubject<any>(null);

  public criteriaIsEmpty$: Observable<boolean> = this.criteriaChanged$.pipe(
    map((criteria) => {
      let isEmpty = false;

      forOwn(criteria, (filterVal, filterKey) => {
        if (filterKey === 'canHasMotive' || !SearchCakesService.FILTERS_API_KEYS_MAP[filterKey]) {
          return;
        }

        if (isArray(filterVal)) {
          isEmpty = isEmpty || some(filterVal, (i) => i.checked);
        }

        if (isBoolean(filterVal)) {
          isEmpty = isEmpty || filterVal;
        }

        if (isString(filterVal)) {
          isEmpty = isEmpty || filterVal.length > 0;
        }
      });

      return !isEmpty;
    }));

  public onPostNumberChanged$: Observable<any> = this.criteriaChanged$.pipe(
    filter((criteria) => Boolean(criteria)),
    distinctUntilChanged((a, b) => Boolean(a.postNumber) === Boolean(b.postNumber))
  );

  public initCriteriaModelFromFilters(filters: IFilterModel, queryParams: IFilterApiModel): IFilterModel {
    const result = {
      hasMotive: queryParams
        ? !!queryParams.hasMotive
        : false,
      postNumber: queryParams.postNumber,
      categoryId: queryParams.categoryId,
      ...filters
    };

    forOwn(filters, (filterVal, filterName) => {
      if (filterName === 'canHasMotive' || !SearchCakesService.FILTERS_API_KEYS_MAP[filterName]) {
        return;
      }

      const queryParamKey = SearchCakesService.FILTERS_API_KEYS_MAP[filterName];
      const params = queryParams[queryParamKey] || [];
      result[filterName] = this._mapQueryParamsOnFilter(filterVal, params, filterName);
    });

    // add foreign query params
    forOwn(queryParams, (val, key) => {
      if (SearchCakesService.FILTERS_API_KEYS_MAP[key]) {
        return;
      }
      result[key] = val;
    });

    if (result.hasMotive && !result.canHasMotive) {
      result.hasMotive = false;
    }

    return result;
  }

  public isCriteriaChanged(aCriteria, bCriteria) {
    let result = true;

    forOwn(bCriteria, (filterVal, filterName) => {
      result = result && !isEqual(aCriteria[filterName], bCriteria[filterName]);
    });

    return result;
  }

  public resetFilters(model: IFilterModel) {
    const cleanModel: IFilterModel = cloneDeep(model);
    forOwn(cleanModel, (filterVal, filterName) => {
      if (filterName === 'canHasMotive' || !SearchCakesService.FILTERS_API_KEYS_MAP[filterName]) {
        return;
      }

      if (isArray(filterVal)) {
        filterVal = filterVal.map((f) => {
          f.checked = false;
          return f;
        });
      }

      if (isBoolean(filterVal)) {
        cleanModel[filterName] = false;
      }

      if (isString(filterVal)) {
        cleanModel[filterName] = '';
      }
    });

    this.criteriaChanged$.next(cleanModel);
    return cleanModel;
  }

  public prepareApiModel(filterModel: IFilterModel, refName: string): IFilterApiModel {
    const result: IFilterApiModel = refName && { refName } || {};

    if (!filterModel) {
      return result;
    }

    forOwn(filterModel, (filterVal, filterKey) => {
      if (filterKey === 'canHasMotive') {
        return;
      }

      const apiKey = SearchCakesService.FILTERS_API_KEYS_MAP[filterKey];

      // for other query params
      if (!apiKey) {
        result[filterKey] = filterVal;
      } else if (isArray(filterVal)) {
        let ids = this._getCriteriaIds(filterVal);

        result[apiKey] = ids.length && ids || null;
      } else if (!isNil(filterVal)) {
        result[apiKey] = filterVal;
      }
    });

    if (filterModel.hasMotive === false) {
      delete result.hasMotive;
    }

    if (!filterModel.postNumber) {
      delete result.postNumber;
    }

    if (!filterModel.categoryId) {
      delete result.categoryId;
    }

    return result;
  }

  public checkIfFiltersExist(filters: IFilterModel, excludingFilters: string[] = []) {
    const model = omit(filters, excludingFilters);

    return get(model, 'allergens.length') || get(model, 'productGroups.length')
      || get(model, 'canHasMotive') || get(model, 'tags.length');
  }

  public getMaxCategoriesToShow(categories, containerWidth: number, widths?: {
    letterWidth?: number, paddings?: number, borderWidth?: number, marginRight?: number, showMoreBtn?: number
  }): number {
    const defaultWidths = { letterWidth: 7, paddings: 40, borderWidth: 4, marginRight: 15, showMoreBtn: 230 };
    const namesAr = categories.map(c => c.name);
    return this.getMaxItemsToShow(namesAr, containerWidth, { ...defaultWidths, ...widths });
  }

  public getMaxGroupsToShow(filters: IFilterModel, containerWidth: number, widths?: {
    letterWidth?: number, paddings?: number, borderWidth?: number, marginRight?: number, showMoreBtn?: number
  }): number {
    const defaultWidths = { letterWidth: 6, paddings: 14, borderWidth: 0, marginRight: 12, showMoreBtn: 220 };
    const namesAr = this._getNamesForGroups(filters);
    return this.getMaxItemsToShow(namesAr, containerWidth, { ...defaultWidths, ...widths });
  }

  public getMaxItemsToShow(namesAr: string[], containerWidth: number,
                           widthValues: {
                             letterWidth: number, paddings: number, borderWidth: number, marginRight: number, showMoreBtn: number,
                           }): number {

    const { letterWidth, paddings, borderWidth, marginRight, showMoreBtn } = widthValues;
    const widthsAr = namesAr.map(name => name.length * letterWidth + paddings + borderWidth + marginRight);

    const maxContainerWidth = containerWidth - showMoreBtn;
    let maxCategoriesToShow = 0;
    let accWidth = 0;

    widthsAr.forEach(width => {
      accWidth += width;
      if (accWidth < maxContainerWidth) {
        maxCategoriesToShow++;
      }
    });

    return maxCategoriesToShow;
  }

  private _getAllergenLabel(allergen) {
    return ALLERGEN_ID_NAME_MAP[allergen.id]
      ? this._translate.instant('allergens.canBeFreeOfSynonyms.' + ALLERGEN_ID_NAME_MAP[allergen.id])
      : this._translate.instant('orderProcess.cakesOverview.searchCakesPanel.allergensPlaceholder',
        { allergen: allergen.name.toLowerCase() });
  }

  private _mapQueryParamsOnFilter(filters, queryParams: any, filterName: string) {
    filters = filters || [];
    if (isString(queryParams)) {
      queryParams = [queryParams];
    }

    queryParams = queryParams.map((id) => +id);

    return filters.map((f) => {
      let label = f.name;

      if (filterName === 'allergens') {
        label = this._getAllergenLabel(f);
      }

      return {
        label,
        value: f.id,
        ref: f,
        checked: includes(queryParams, +f.id)
      };
    });
  }

  private _getCriteriaIds(criteria: any): number[] {
    return criteria
      .filter((c) => c.checked)
      .map((c) => +c.value);
  }

  private _getNamesForGroups(filters: IFilterModel): string[] {
    const namesAr = [];

    if (filters.allergens && filters.allergens.length) {
      namesAr.push(this._translate.instant('orderProcess.cakesOverview.searchCakesPanel.allergens'));
    }

    if (filters.canHasMotive) {
      namesAr.push(this._translate.instant('orderProcess.cakesOverview.searchCakesPanel.motive'));
    }

    if (filters.productGroups && filters.productGroups.length) {
      filters.productGroups.forEach(g => {
        namesAr.push(g.label);
      });
    }

    return namesAr;
  }
}
